import React, { useState, useEffect, useRef } from 'react';
import EventEmitter from 'react-native-eventemitter';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { trackingEvent } from '../../analytics';
import { getBasicAmplitudEventProperties } from '../../analytics/utils';
import { AMPLITUDE_SERVICE } from '../../analytics/constants';
import { treePaneService } from '../../services/activityTreeSettings.service';
import notificationSystemV2 from '../DesignSystem/NotificationSystemV2/NotificationSystemV2';
import { InfoIcon } from '../../icons';
import * as Sentry from '@sentry/react';

const TIMEFRAME_RANGE_MAP = ['1', '2', '4', '6', '8', 'date', 'custom'];

export const useTimeFrameFilter = (toogleActivityTreePane) => {
  const { t } = useTranslation();

  const defaultView = window.defaultView?.current;

  const optionsPopoverRef = useRef();

  const currentRange = defaultView?.activityTreeSettings?.range;
  const currentTimeframeRange =
    defaultView?.activityTreeSettings?.timeframeRange;
  const currentActivitieIds = defaultView?.activityTreeSettings?.activitieIds;

  const { mutateAsync } = useMutation({
    mutationFn: (data) =>
      treePaneService.selectedActivityTreeByDateRange({
        ...data,
        viewId: defaultView?.id,
        scheduleId: defaultView?.sectorId
      }),
    retry: 0
  });

  const [selectedItem, setSelectedItem] = useState();
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);

  useEffect(() => {
    getCurrentSelectedItem();
  }, [currentTimeframeRange, currentRange]);

  useEffect(() => {
    if (currentActivitieIds?.length === 0) {
      notificationSystemV2({
        key: 'time-frame-empty-activities',
        type: 'warning',
        message: t(
          'lookahead_visualizationOpt.time_frame_empty_activities_message'
        )
      });
    }
  }, [currentTimeframeRange, currentRange]);

  useEffect(() => {
    if (!defaultView) {
      Sentry.withScope((scope) => {
        scope.setContext('useTimeFrameFilter', {
          message: 'defaultView is not defined'
        });
        Sentry.captureException('defaultView is not defined');
      });
    }
  }, [defaultView]);

  const handleSelectItem = async (item) => {
    try {
      setSelectedItem(item);

      const response = await mutateAsync({ range: item });

      if (response.isError) {
        return;
      }

      EventEmitter.emit('HIDE_PLANIFICATION');

      handleEventTracking(item);

      if (response.data.isDefaultActivities) {
        notificationSystemV2({
          key: 'time-frame-empty-activities',
          type: 'warning',
          message: t(
            'lookahead_visualizationOpt.time_frame_empty_activities_message'
          )
        });
      }

      if (!response.data.isPartialSavingActivities) {
        return;
      }

      notificationSystemV2({
        key: 'partially-saved-activities',
        type: 'warning',
        message: t('lookahead_visualizationOpt.time_frame_exceed_limit_message')
      });

      trackingEvent(
        'timeframe_filter_data_limit_reached',
        {
          ...getBasicAmplitudEventProperties(),
          viewId: defaultView?.id,
          source: 'timeframe',
          timeframe_selected: item,
          total_items_requested: response.data.totalActivities,
          max_items_loaded: response.data.maxItems
        },
        AMPLITUDE_SERVICE
      );
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setContext('handle Select Item', {
          message: err.message
        });
        Sentry.captureException(err);
      });
    }
  };

  const handleOpenDateRangeModal = () => {
    setIsDateRangeModalOpen(true);
  };

  const handleEventTracking = (item) => {
    trackingEvent(
      'timeframe_filter_toolbar',
      {
        ...getBasicAmplitudEventProperties(),
        filter_option: item
      },
      AMPLITUDE_SERVICE
    );
  };

  const handleClosePopover = () => {
    if (optionsPopoverRef.current) {
      optionsPopoverRef.current.tooltip.state.visible = false;
    }
  };

  const getCurrentSelectedItem = () => {
    setSelectedItem(currentTimeframeRange);
  };

  const isActiveItem = (item) => selectedItem === item;

  const getItemsPopover = () => {
    const createBaseItem = (key, name, onClick) => ({
      key,
      active: isActiveItem(key),
      disabled: false,
      group: [],
      icon: null,
      name: t(`lookahead_visualizationOpt.time_frame_filter_${name}`),
      onClick,
      subitems: [],
      onClickWhenActive: true
    });

    const createSpecialItem = (key, name, onClick) => ({
      ...createBaseItem(key, name, onClick),
      informationIcon: true,
      additionalIcon: <InfoIcon />,
      additionalIconTooltip: {
        content: t(
          `lookahead_visualizationOpt.time_frame_filter_${key}_range_tooltip`
        )
      },
      iconPosition: 'right'
    });

    const weekItems = TIMEFRAME_RANGE_MAP.filter(
      (key) => !['date', 'custom'].includes(key)
    ).map((key) => createBaseItem(key, key, () => handleSelectItem(key)));

    const dateItem = createSpecialItem('date', 'date', () => {
      handleClosePopover();
      handleOpenDateRangeModal();
    });

    const customItem = createSpecialItem('custom', 'custom', () => {
      handleClosePopover();
      setSelectedItem('custom');
      toogleActivityTreePane(true);
      handleEventTracking('custom');
    });

    return [...weekItems, dateItem, customItem];
  };

  return {
    optionsPopoverRef,
    isDateRangeModalOpen,
    selectedItem,
    rollingStatus: defaultView?.rollingStatus || 'disabled',
    getItemsPopover,
    setIsDateRangeModalOpen
  };
};
